
import { Component, Prop, Vue } from 'vue-property-decorator'

// Review body
import { uniqBy } from 'lodash-es'
import { deserialize } from '@shared/jsona'
import Button from '~/components/common/buttons/DashboardButton.vue'
import CloseCrossCtrl from '~/components/common/ctrls/CloseCrossCtrl.vue'
import ApplicationReviewHeader from '~/components/common/modals/application-review/ApplicationReviewHeader.vue'
import Header from '~/components/common/modals/application-review/ApplicationReviewModalHeader.vue'
import BodyApplicationReview from '~/components/common/modals/application-review/BodyApplicationReview.vue'
import DescriptionItem from '~/components/common/modals/application-review/DescriptionItem.vue'
import RequirementApplicationReview from '~/components/common/modals/application-review/RequirementApplicationReview.vue'
import CoregConsentCheckbox from '~/components/common/modals/application-review/previewModal/CoregConsentCheckbox.vue'
import PreviewModal from '~/components/common/modals/application-review/previewModal/PreviewModal.vue'
import BasicModal from '~/components/common/modals/basic/Basic.vue'
import FooterBasic from '~/components/common/modals/basic/FooterBasic.vue'
import {
  letterApplicationRequirementTooltip,
  onlineApplicationRequirementTooltip
} from '~/components/common/tooltip-texts'
import { manageWidgets } from '~/lib/utils/utils'
import { DeserializedScholarship } from '~/types/jsonapi/scholarship'

@Component({
  name: 'ApplicationReview',
  components: {
    BasicModal,
    FooterBasic,
    Button,
    Header,
    ApplicationReviewHeader,
    BodyApplicationReview,
    DescriptionItem,
    RequirementApplicationReview,
    PreviewModal,
    CloseCrossCtrl,
    CoregConsentCheckbox
  }

})
export default class ApplicationReview extends Vue {
  @Prop() reviewFields!: Review.Fields[]
  @Prop() scholarshipId!: number
  @Prop() scholarshipName!: string
  @Prop() providerName!: string
  @Prop() applicationType!: string
  @Prop() applicationData!: Review.ApplicationData
  @Prop() requirements!: Review.Requirements
  @Prop() hasRequirements!: boolean
  @Prop() apply!: any
  @Prop({ type: String, default: '' }) coregCheckboxText: string

  private manageWidgets = manageWidgets

  readonly REVIEW_TYPE = {
    EMAIL: 'email',
    ONLINE: 'online',
    SUNRISE: 'sunrise'
  }

  readonly REQ_ALIASES = {
    PHOTO: 'photo',
    VIDEO: 'video',
    QUESTION: 'question',
    LINK: 'link',
    DOCUMENT: 'document',
    SURVEY: 'survey',
    SPECIAL_ELIGIBILITY: 'special eligibility',
    ESSAY: 'essay',
    GOAL: 'goal'
  }

  isChecked = false

  scholarship: DeserializedScholarship = null

  created() {
    this.$api.scholarship
      .get({
        id: this.scholarshipId
      })
      .then((res) => {
        this.scholarship = deserialize<DeserializedScholarship>(res.rawRes)
      })
  }

  getLocalizedTimestamp(input?: string) {
    if (!input) {
      return
    }

    const date = new Date(input)

    return date.toLocaleDateString('en-US', {
      dateStyle: 'short',
      timeZone: this.scholarship.timezone
    })
  }

  get started() {
    return this.getLocalizedTimestamp(this.scholarship?.createdDate)
  }

  get deadline() {
    return this.getLocalizedTimestamp(this.scholarship?.expirationDate)
  }

  get showSweepstakesDisclaimer() {
    return (
      !this.hasRequirements &&
      (this.scholarship?.isSponsored ||
        ['scholarshipowl', 'future minds fund'].includes(
          this.scholarship?.providerName.toLowerCase()
        ))
    )
  }

  get tooltipText() {
    return this.applicationType === this.REVIEW_TYPE.EMAIL
      ? letterApplicationRequirementTooltip
      : onlineApplicationRequirementTooltip
  }

  get appName() {
    return (req: Review.Requirement) => {
      if (this.applicationType === this.REVIEW_TYPE.EMAIL) { return [req.data.file] }
      // @ts-ignore
      if (req.label.toLocaleLowerCase() === 'survey') { return req.data.map(item => item.survey.question) }

      return req.title ? [req.title] : ['']
    }
  }

  get denoteIcon() {
    const icons = {
      [this.REQ_ALIASES.PHOTO]: { name: 'photo-camera', width: '22px' },
      [this.REQ_ALIASES.VIDEO]: { name: 'video-camera', width: '22px' },
      [this.REQ_ALIASES.QUESTION]: { name: 'question-circle', width: '22px' },
      [this.REQ_ALIASES.LINK]: { name: 'link-sync', width: '22px' },
      [this.REQ_ALIASES.DOCUMENT]: { name: 'folded-document-right', width: '18px' },
      [this.REQ_ALIASES.SURVEY]: { name: 'assignment-turned-in', width: '18px' },
      [this.REQ_ALIASES.SPECIAL_ELIGIBILITY]: { name: 'poll-document', width: '21px' },
      [this.REQ_ALIASES.ESSAY]: { name: 'curved-document', width: '20px' },
      [this.REQ_ALIASES.GOAL]: { name: 'verified-goal', width: '22px' }
    }

    return (reqAlias: Review.ReqAlias) => {
      reqAlias = reqAlias.toLowerCase() as Review.ReqAlias
      return icons[reqAlias]
    }
  }

  get previewIcon() {
    const icons = {
      [this.REQ_ALIASES.PHOTO]: { name: 'eye-open', width: '22px' },
      [this.REQ_ALIASES.VIDEO]: { name: 'download-arrow', width: '18px' },
      [this.REQ_ALIASES.QUESTION]: { name: 'eye-open', width: '22px' },
      [this.REQ_ALIASES.LINK]: { name: 'link-outside', width: '16px' },
      [this.REQ_ALIASES.DOCUMENT]: { name: 'download-arrow', width: '18px' },
      [this.REQ_ALIASES.SURVEY]: { name: 'eye-open', width: '22px' },
      [this.REQ_ALIASES.SPECIAL_ELIGIBILITY]: { name: 'eye-open', width: '22px' },
      [this.REQ_ALIASES.ESSAY]: { name: 'download-arrow', width: '18px' },
      [this.REQ_ALIASES.GOAL]: { name: 'eye-open', width: '22px' }
    }

    return (reqAlias: Review.ReqAlias) => {
      reqAlias = reqAlias.toLowerCase() as Review.ReqAlias
      return icons[reqAlias]
    }
  }

  private activePreview = null

  mounted() {
    this.manageWidgets(true)
  }

  beforeDestroy() {
    if (!this.$route.params.cardId) {
      this.manageWidgets(false)
    }
  }

  applyPreview(req: Review.Requirement) {
    const reqAlias = req.label.toLowerCase().trim()
    const props: any = {
      requirementAlias: reqAlias,
      requirementTitle: req.title
    }

    switch (reqAlias) {
      case this.REQ_ALIASES.QUESTION:
        props.question = {
          question: req.description,
          answer: req.data.val
        }
        this.activePreview = props
        break

      case this.REQ_ALIASES.SURVEY:
        props.surveys = req.data
        this.activePreview = props
        break

      case this.REQ_ALIASES.SPECIAL_ELIGIBILITY:
        props.eligibility = {
          description: req.description,
          // @ts-ignore
          text: req.text
        }
        this.activePreview = props
        break

      case this.REQ_ALIASES.GOAL:
        props.goal = {
          title: req.title,
          description: req.description
        }
        this.activePreview = props
        break

      case this.REQ_ALIASES.PHOTO:
        // @ts-ignore
        props.photo = '/file/download/' + req.data.accountFileId
        this.activePreview = props
        break

      case this.REQ_ALIASES.LINK:
        window.open(req.data.val, '_blank')
        break

      default:
        // @ts-ignore
        window.open(req.data.accountFileId
        // @ts-ignore
          ? '/file/download/' + req.data.accountFileId
        // @ts-ignore
          : '/rest/v1/application/text/download/' + req.data.applicationTextId)
    }
  }

  hidePreview() {
    this.activePreview = null
  }

  applyPreviewTrack(req) {
    const reqAlias = req.label.toLowerCase().trim()
    const eventName = reqAlias === this.REQ_ALIASES.ESSAY ||
      reqAlias === this.REQ_ALIASES.DOCUMENT ||
      reqAlias === this.REQ_ALIASES.VIDEO
      ? this.$mixpanel.events.APPLICATION_PREVIEW_REQ_DOWNLOAD
      : this.$mixpanel.events.APPLICATION_PREVIEW_REQ_VIEW

    this.$mixpanel.track(eventName, { 'requirement type': reqAlias })
  }

  get dedupedReviewFields() {
    return uniqBy(this.reviewFields, 'label')
  }
}
